import {
  getObservabilitySessionId,
  OBSERVABILITY_SESSION_ID_KEY,
} from '~/services/observability/correlation'
import { addBreadCrumb, captureException } from '~/services/sentry'
import { cloudwatchLogGroup, getCloudwatchLogGroupName } from '~/utils/common'

import { Logger as ILogger } from '@mangovoice/types'

import CloudWatchLogger from './CloudwatchLogger'
import { formatConsoleMessage, LogLevel } from './Logger.console'

export interface CloudwatchMetadata {
  pbxId: number
  userId: number
}

class Logger implements ILogger {
  private readonly tag: string
  private readonly cloudwatchMetadata?: CloudwatchMetadata

  constructor(tag: string, metadata?: CloudwatchMetadata) {
    this.tag = tag
    this.cloudwatchMetadata = metadata
  }

  private log(
    level: LogLevel,
    message: string,
    error: Error | null = null,
    args?: Record<string, unknown>,
  ): void {
    const logMessage = formatConsoleMessage(this.tag, level, message, error, {
      ...args,
    })

    // TODO: logging everything for now until we get a better system for users to set their log levels.
    console.log(logMessage)

    const timestamp = new Date().toISOString()

    switch (level) {
      case LogLevel.DEBUG:
        // do nothing in production
        break
      case LogLevel.INFO:
        addBreadCrumb({
          level: 'info',
          category: this.tag,
          message,
          data: {
            ...args,
            timestamp,
          },
        })
        break
      case LogLevel.WARN:
        addBreadCrumb({
          level: 'warning',
          category: this.tag,
          message,
          data: {
            ...args,
            timestamp,
          },
        })
        break
      case LogLevel.ERROR: {
        // Ensure we always pass an Error instance to Sentry
        let errorToCapture: Error
        if (error instanceof Error) {
          errorToCapture = error
        } else if (error !== null && typeof error === 'object') {
          // Handle plain objects that might have been passed as errors
          const errorObj = error as Record<string, unknown>
          errorToCapture = new Error(
            'message' in errorObj ? String(errorObj.message) : message,
          )
        } else {
          errorToCapture = new Error(message)
        }
        captureException(errorToCapture, {
          extra: {
            ...args,
            timestamp,
          },
        })
        break
      }
      default:
        break
    }

    if (this.cloudwatchMetadata && level !== LogLevel.DEBUG) {
      const timestamp = new Date().toISOString()
      // session_id is the cross-tool correlation id (Sentry tag + PostHog
      // super property); it sits before the free-form message so left-to-right
      // pipe parsing stays deterministic.
      const cwMessage = `${timestamp} | ${this.tag} | ${level} | ${OBSERVABILITY_SESSION_ID_KEY}=${getObservabilitySessionId()} | ${message}`
      CloudWatchLogger.getInstance(
        cloudwatchLogGroup,
        `pbx/${this.cloudwatchMetadata.pbxId}/user/${this.cloudwatchMetadata.userId}/user.log`,
      ).log(cwMessage)
    }
  }

  /**
   * Writes to console in __DEV__ mode, does not send to Sentry
   * Swallows everything in __PROD__ mode
   * @param message the string to log
   * @param args
   */
  public debug(message: string, args?: Record<string, unknown>): void {
    this.log(LogLevel.DEBUG, message, null, args)
  }

  /**
   * Writes to console in __DEV__ mode
   * Adds a Sentry Breadcrumb in __PROD__ mode
   * @param message string to log
   * @param args
   */
  public info(message: string, args?: Record<string, unknown>): void {
    this.log(LogLevel.INFO, message, null, args)
  }

  /**
   * Writes to console in __DEV__ mode
   * @param message string to log
   * @param args
   */
  public warn(message: string, args?: Record<string, unknown>): void {
    this.log(LogLevel.WARN, message, null, args)
  }

  /**
   * Writes to console in __DEV__ mode
   * Sends to Sentry in __PROD__ mode
   * @param message
   * @param error
   * @param args
   */
  public error(
    message: string,
    error: Error | null = null,
    args?: Record<string, unknown>,
  ): void {
    this.log(LogLevel.ERROR, message, error, args)
  }

  public async flush(): Promise<void> {
    if (this.cloudwatchMetadata) {
      await CloudWatchLogger.getInstance(
        getCloudwatchLogGroupName(),
        `pbx/${this.cloudwatchMetadata.pbxId}/user/${this.cloudwatchMetadata.userId}/user.log`,
      ).flush()
    }
  }
}

export default Logger
